import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useLocalization } from '@/hooks/useLocalization';
import { supabase } from '@/integrations/supabase/client';
import { googleWorkspace, type LabelPick, type SyncResult } from '@/lib/googleWorkspace';
import { GoogleWorkspaceError, isBroadSystemLabel, isGoogleAuthUrl } from '@/lib/googleWorkspaceLogic';

const QK = ['google-workspace'] as const;

export function GoogleWorkspacePanel() {
  const { t } = useLocalization();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<Record<string, LabelPick>>({});
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const status = useQuery({ queryKey: [...QK, 'status'], queryFn: googleWorkspace.status });
  const labels = useQuery({ queryKey: [...QK, 'labels'], queryFn: googleWorkspace.listLabels, enabled: picking });
  const projects = useQuery({
    queryKey: [...QK, 'projects'],
    enabled: picking,
    queryFn: async () => {
      const { data, error } = await supabase.from('projects_sync').select('id,name').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: QK });
  const fail = (e: unknown) => {
    // The server says the grant is gone: refetch status so the Reconnect button appears.
    if (e instanceof GoogleWorkspaceError && e.code === 'needs_reauth') refresh();
    toast({
      variant: 'destructive',
      title: t('Google'),
      description: e instanceof Error ? e.message : t('Something went wrong. Please try again.'),
    });
  };
  const reportSync = (r: SyncResult) => {
    if ('skipped' in r) {
      if (r.skipped === 'sync_in_progress') toast({ title: t('Google'), description: t('A sync is already running') });
      else { toast({ title: t('Google'), description: t('Google needs to be reconnected before syncing.') }); }
    } else {
      toast({ title: t('Synced'), description: `${r.fetched} ${t('emails')}, ${r.upserted} ${t('events')}, ${r.transcripts} ${t('transcripts')}` });
    }
    refresh();
  };

  const connect = useMutation({
    mutationFn: googleWorkspace.authUrl,
    onSuccess: ({ url }) => {
      if (!isGoogleAuthUrl(url)) return fail(new Error(t('Unexpected response from the server.')));
      window.location.assign(url);
    },
    onError: fail,
  });
  const save = useMutation({
    mutationFn: async () => {
      // Deselecting a previously selected label also removes mail already synced from it.
      const removing = (status.data?.labels ?? []).some((l) => !picked[l.gmail_label_id]);
      const saved = await googleWorkspace.saveLabels(Object.values(picked), removing);
      let sync: SyncResult | null = null;
      let syncError: unknown = null;
      try { sync = await googleWorkspace.syncNow(); } catch (e) { syncError = e; }
      return { saved, sync, syncError };
    },
    onSuccess: ({ saved, sync, syncError }) => {
      const desc = saved.purged > 0
        ? t('{n} previously synced emails were removed.').replace('{n}', String(saved.purged))
        : saved.pendingPurge
          ? t('Mail from removed labels will be removed from Masira during the next sync.')
          : undefined;
      toast({ title: t('Labels saved'), description: desc });
      setPicking(false);
      if (syncError) fail(syncError);
      else if (sync && 'skipped' in sync) {
        if (sync.skipped === 'sync_in_progress') toast({ title: t('Google'), description: t('A sync is already running') });
        else toast({ title: t('Google'), description: t('Sync skipped: reconnect Google') });
      }
      refresh();
    },
    onError: fail,
  });
  const sync = useMutation({ mutationFn: googleWorkspace.syncNow, onSuccess: reportSync, onError: fail });
  const disconnect = useMutation({
    mutationFn: () => googleWorkspace.disconnect(true),
    onSuccess: () => {
      toast({ title: t('Google disconnected'), description: t('Synced Google mail and events were removed from Masira.') });
      setPicking(false);
      refresh();
    },
    onError: fail,
  });

  const s = status.data;
  const startPicking = () => {
    setPicked(Object.fromEntries((s?.labels ?? []).map((l) => [l.gmail_label_id, { id: l.gmail_label_id, name: l.label_name, projectId: l.project_id }])));
    setPicking(true);
  };
  const toggle = (id: string, name: string, on: boolean) =>
    setPicked((p) => {
      const n = { ...p };
      if (on) n[id] = { id, name }; else delete n[id];
      return n;
    });

  return (
    <div className="p-4 border border-border rounded-lg space-y-4" data-testid="google-workspace-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center"><Mail className="h-5 w-5 text-red-500" aria-hidden="true" /></div>
          <div>
            <p className="font-medium">{t('Google (Gmail, Calendar, Meet)')}</p>
            <p className="text-sm text-muted-foreground">{s?.connected ? s.email : t('Connect your Google account')}</p>
          </div>
        </div>
        {status.isLoading ? <Loader2 className="h-4 w-4 animate-spin" role="status" aria-label={t('Loading')} />
          : status.isError ? <Button size="sm" variant="outline" onClick={() => status.refetch()}>{t('Retry')}</Button>
          : !s?.connected ? <Button variant="outline" onClick={() => connect.mutate()} disabled={connect.isPending}>{connect.isPending ? t('Redirecting…') : t('Connect')}</Button>
          : s.status === 'needs_reauth' ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => connect.mutate()} disabled={connect.isPending}>{t('Reconnect')}</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDisconnect(true)} disabled={disconnect.isPending}>{t('Disconnect')}</Button>
            </div>
          )
          : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>
                <RefreshCw className={`h-4 w-4 mr-1 ${sync.isPending ? 'animate-spin' : ''}`} aria-hidden="true" />{t('Sync now')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDisconnect(true)} disabled={disconnect.isPending}>{t('Disconnect')}</Button>
            </div>
          )}
      </div>
      {status.isError && <p className="text-sm text-destructive" role="alert">{t('Could not load the Google connection status.')}</p>}
      {!status.isLoading && !status.isError && !s?.connected && (
        <p className="text-sm text-muted-foreground">{t('Masira only reads Gmail messages that carry the labels you choose, plus your calendar events and Meet transcripts. Nothing else in your mailbox is read or stored. You can disconnect at any time and synced data is removed.')}</p>
      )}

      {s?.connected && (
        <div className="space-y-2">
          {s.status === 'needs_reauth' && (
            <p className="text-sm text-amber-600 flex items-center gap-1" role="alert"><AlertTriangle className="h-4 w-4" aria-hidden="true" />{t('Google access expired. Reconnect to resume syncing.')}</p>
          )}
          <p className="text-sm text-muted-foreground">{t('Masira only reads mail that has one of the labels you choose. Everything else in your mailbox stays out of Masira.')}</p>
          {!picking && (
            <div className="flex flex-wrap items-center gap-2">
              {(s.labels ?? []).length === 0 && <Badge variant="outline">{t('No labels selected: no email is synced')}</Badge>}
              {(s.labels ?? []).map((l) => <Badge key={l.gmail_label_id} variant="secondary">{l.label_name}</Badge>)}
              <Button size="sm" variant="outline" onClick={startPicking}>{t('Choose labels')}</Button>
            </div>
          )}
          {picking && (
            <div className="space-y-2">
              {labels.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{t('Loading labels…')}</div>}
              {labels.isError && (
                <div className="flex items-center gap-2" role="alert">
                  <p className="text-sm text-destructive">{t('Could not load your Gmail labels.')}</p>
                  <Button size="sm" variant="outline" onClick={() => labels.refetch()}>{t('Retry')}</Button>
                </div>
              )}
              {projects.isError && (
                <div className="flex items-center gap-2" role="alert">
                  <p className="text-sm text-destructive">{t('Could not load projects.')}</p>
                  <Button size="sm" variant="outline" onClick={() => projects.refetch()}>{t('Retry')}</Button>
                </div>
              )}
              <div className="max-h-64 overflow-auto space-y-1">
                {(labels.data?.labels ?? []).map((l) => {
                  const broad = isBroadSystemLabel(l);
                  const on = !!picked[l.id];
                  const warnId = `lbl-warn-${l.id}`;
                  return (
                    <div key={l.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`lbl-${l.id}`}
                        checked={on}
                        aria-describedby={broad ? warnId : undefined}
                        onCheckedChange={(v) => toggle(l.id, l.name, v === true)}
                      />
                      <label htmlFor={`lbl-${l.id}`} className="text-sm flex-1">{l.name}</label>
                      {broad && (
                        <span id={warnId} className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" aria-hidden="true" />{t('Syncs a lot of mail')}
                        </span>
                      )}
                      {on && (
                        <select
                          className="text-xs border rounded px-1 py-0.5 bg-background"
                          aria-label={`${t('Project for this label')}: ${l.name}`}
                          value={picked[l.id]?.projectId ?? ''}
                          onChange={(e) => setPicked((p) => ({ ...p, [l.id]: { ...p[l.id], projectId: e.target.value || null } }))}
                        >
                          <option value="">{projects.isLoading ? t('Loading projects…') : t('Auto-detect project')}</option>
                          {(projects.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || labels.isLoading || labels.isError}>{save.isPending ? t('Saving…') : t('Save and sync')}</Button>
                <Button size="sm" variant="ghost" onClick={() => setPicking(false)} disabled={save.isPending}>{t('Cancel')}</Button>
              </div>
            </div>
          )}
          {s.lastSyncedAt && <p className="text-xs text-muted-foreground">{t('Last synced')}: {new Date(s.lastSyncedAt).toLocaleString()}</p>}
          {s.lastError && <p className="text-xs text-destructive">{s.lastError}</p>}
        </div>
      )}

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Disconnect Google?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('Synced Google mail and events will be removed from Masira. Your Google data is not changed.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => disconnect.mutate()}>{t('Disconnect')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
